// The collapsible sticky RSVP panel. Port of _sticky_panel.php +
// public/assets/js/sticky_panel.js, which did not survive the migration — see
// docs/migration-gaps.md item 5.
//
// One component, two hosts, and since 2026-09-10 they ask it for different
// things: the home "Next up" zone gets the full picker (every open event across
// every group, unanswered first), and the group feed gets `dayOfOnly` — a box
// that appears only on the day of one of that group's events. The group feed
// already renders its events as cards, so an upcoming picker there was a second
// RSVP control for every one of them.
//
// It stays pinned under the site header while the feed scrolls, so the thing you
// are most likely to need to answer never scrolls away. That is the surface the
// day-of confirmation was answered from, and the reason the day-of box survives
// in both home modes.
//
// The panel does TWO jobs that want opposite ordering — day-of confirmation
// (today's event, pinned) and "what needs my answer" (unanswered, whenever it
// is). They are reconciled by keeping list MEMBERSHIP and DEFAULT SELECTION
// separate: the list is ordered unanswered-first, and the selection still lands
// on today's event when there is one. See docs/plan-next-up.md.
//
// It is chrome around `rsvpControl()`, not a reimplementation of it. The PHP
// version hand-rolled its RSVP buttons and then fetched the headcount over a
// second AJAX call per event; here the control already round-trips without
// navigation and the counts already arrive with the event, so there is nothing
// to fetch. The control also brings day-of confirmation, which the PHP panel
// never had.

import { el } from '../dom.js';
import {
  dayOfEvents,
  defaultPinnedIndex,
  fmtEventSchedule,
  isEventUnanswered,
  orderPinnableEvents,
  rsvpStatusLabel,
  unansweredCount,
  type EventSummary,
  type RsvpCounts,
} from '@msc/shared';
import { rsvpControl } from './rsvpControl.js';
import { countsFromSummary } from './eventCard.js';

export interface StickyPanelOptions {
  /**
   * The events to offer, already through `orderPinnableEvents()` — unanswered
   * first, then soonest. An empty list renders nothing at all: the panel is
   * never an empty box.
   */
  events: EventSummary[];
  /** The viewer's display zone. */
  viewerZone: string;
  /**
   * Group name per event, for the picker. Only wanted on the home page, where
   * the events span groups; inside one group it is noise.
   */
  groupNames?: Map<number, string>;
  /**
   * Called after a successful RSVP or day-of confirm, so a host showing the same
   * event as a card can keep it in step. The PHP version called this
   * `mscRefreshEventCard`.
   */
  onRsvpChange?: (eventId: number, counts: RsvpCounts) => void;
  /**
   * Narrow the panel to TODAY only, and title it as the day-of box.
   *
   * This is what the group feed gets. A group feed shows that group's events as
   * cards, so an upcoming picker there duplicated every one of them — two RSVP
   * controls for the same event on one screen, which is the rule `home.ts`
   * carries and which the group feed was quietly breaking.
   *
   * ⚠ On the day of an event the group feed WILL show it in this box and as its
   * announcement card, each with an RSVP control. That one narrow case is
   * deliberate and must not be "fixed": the card announces and takes an advance
   * RSVP, this box is the day-of confirmation surface, which is the product's
   * differentiator and must not be scrollable-past. Decided with Will
   * 2026-09-10; see docs/plan-next-up.md §3e.
   */
  dayOfOnly?: boolean;

  /**
   * The instant "today" is resolved against. Defaults to `Date.now()` read
   * afresh on every narrowing, which is what a long-lived group feed wants.
   *
   * Feed mode passes it explicitly because the page renders the box and the
   * cards as a PARTITION of one list — `dayOfEvents()` here, `nonDayOfEvents()`
   * there — and "today" resolves in the event's AUTHORING zone. Two Date.now()
   * calls microseconds apart normally agree, but across a midnight boundary
   * they need not, and disagreeing puts an event in both lists or in neither.
   * One tick, one clock; the same rule cron learned the hard way twice.
   *
   * Passing it FREEZES the instant for `setEvents()` too. That is fine for home,
   * which never calls it, and is why the group feed leaves this unset.
   */
  nowMs?: number;
}

// The PHP panel also carried its own "Add a comment to the feed…" composer. It
// is deliberately NOT ported: on the group feed it sat directly above the feed's
// own composer, both posting an identical general post to the same group. One
// composer per page.

export interface StickyPanelHandle {
  root: HTMLElement;
  /** Replace the event list, e.g. after one is created live. */
  setEvents(events: EventSummary[]): void;
}

export function stickyPanel(opts: StickyPanelOptions): StickyPanelHandle {
  const dayOfOnly = opts.dayOfOnly ?? false;

  // The narrowing lives HERE, not in each caller, so `setEvents()` — which the
  // group feed calls when an event is created live — cannot reintroduce the
  // upcoming picker on a surface that is meant to be day-of only.
  function now(): number {
    return opts.nowMs ?? Date.now();
  }

  function narrow(list: EventSummary[]): EventSummary[] {
    return dayOfOnly ? dayOfEvents(list, now()) : list;
  }

  let events = narrow(opts.events);
  // The default selection, NOT 0: today's event, else the soonest unanswered,
  // else the soonest. Membership and selection are separate decisions, and that
  // separation is what let the list widen without costing day-of confirmation.
  let idx = defaultPinnedIndex(events, now());
  let collapsed = false;

  const title = el('a', { class: 'sticky-title', href: '#' });
  const when = el('span', { class: 'sticky-when' });
  const status = el('span', { class: 'sticky-status', hidden: true });
  // The backlog, separate from the selected event's own chip: the head is all
  // that survives a collapse, so it has to describe how many events are waiting,
  // not just the one on screen.
  //
  // A LINK to /events, where the waiting events are listed. It was a span until
  // 2026-10-07, so a tap fell through to the head's click handler and collapsed
  // the box in place -- the opposite of "show me the others". The head handler
  // ignores anchors, so making it one is the whole fix.
  const backlog = el('a', { class: 'sticky-backlog', href: '/events', hidden: true });
  const toggle = el('button', {
    class: 'sticky-toggle',
    type: 'button',
    'aria-expanded': 'true',
    'aria-label': 'Collapse',
  }, '▴');

  // ONE event: the top line is that event, a linked title and its schedule.
  // SEVERAL: the picker takes the title's place and carries the details in each
  // option, so the top line both names the current event and is how you switch.
  // The two are mutually exclusive — never a title AND a picker.
  const picker = el('select', {
    class: 'sticky-pick',
    'aria-label': 'Which event',
    hidden: true,
  }) as HTMLSelectElement;

  // A plain-text "N events" beside the picker. A bare <select> reads as a title,
  // and a newcomer has no reason to suspect there is a second event behind it.
  const count = el('span', { class: 'sticky-count', hidden: true });

  // On a day-of box the calendar glyph is not enough: the box only appears on
  // the day, so it has to say why it is there.
  const head = el('div', { class: 'sticky-head' }, [
    ...(dayOfOnly
      ? [el('span', { class: 'sticky-tag' }, 'Today')]
      : [el('span', { class: 'sticky-cal', 'aria-hidden': 'true' }, '📅')]),
    title,
    when,
    count,
    picker,
    status,
    backlog,
    toggle,
  ]);

  const rsvpSlot = el('div', { class: 'sticky-rsvp' });
  // The panel's own chat row, as the PHP panel had. Rebuilt on every render
  // because both the target event and its unread count change with the picker.
  const chatSlot = el('p', { class: 'small sticky-chat' });
  // The event's own capacity message, written by rsvpControl and placed by the
  // body's grid at the bottom right (Will, 2026-10-16).
  const note = el('p', { class: 'sticky-note', hidden: true });
  const body = el('div', { class: 'sticky-body' }, [rsvpSlot, chatSlot, note]);

  const root = el('section', { class: 'sticky-panel', hidden: true }, [head, body]);

  // --- Rendering ----------------------------------------------------------

  function scheduleLabel(event: EventSummary): string {
    // fmtEventSchedule, never fmtEventDateTime: an all-day event has no time to
    // shift, and converting its midnight moves the DAY for any viewer west of
    // the authoring zone.
    return fmtEventSchedule(event.eventDate, event.startTime, event.timeZone, opts.viewerZone);
  }

  function render(): void {
    if (events.length === 0) {
      root.hidden = true;
      return;
    }
    if (idx >= events.length) idx = 0;

    const event = events[idx];
    if (!event) {
      root.hidden = true;
      return;
    }

    root.hidden = false;

    const label = scheduleLabel(event);
    const groupName = opts.groupNames?.get(event.groupId);
    const several = events.length > 1;

    // The title line and the picker are the same slot in two states.
    title.hidden = several;
    when.hidden = several;
    picker.hidden = !several;
    count.hidden = !several;
    count.textContent = `${events.length} events`;

    title.textContent = event.title;
    title.setAttribute('href', `/events/${event.eventId}`);
    when.textContent = [label, groupName].filter(Boolean).join(' · ');

    // The status chip is the whole point of the collapsed state: an
    // at-a-glance read of whether you have answered.
    //
    // ⚠ UNANSWERED IS THE LOUD STATE. This had it backwards until 2026-09-10:
    // answering got you a badge and NOT answering got silence, so the one state
    // that needs action rendered nothing. The head is all that survives a
    // collapse, which is exactly when a nudge has to work.
    // Through the shared rule rather than `myStatus` directly, so `maybe`
    // stays ANSWERED here and in the picker without the two drifting apart.
    const answered = isEventUnanswered(event) ? null : event.myStatus;
    // An ANSWERED event shows no chip (Will, 2026-10-16): the highlighted RSVP
    // button below already says it, and the head is kept as small as it can be.
    // Only the unanswered state, the one needing action, stays loud.
    if (answered === null) {
      status.textContent = 'Needs your answer';
      status.className = 'sticky-status st-unanswered';
    }
    status.hidden = answered !== null;

    // Only when there is a backlog BEYOND the one being shown — "1 more needs
    // an answer" beside a chip already saying this one does is noise.
    const waiting = unansweredCount(events);
    const others = waiting - (isEventUnanswered(event) ? 1 : 0);
    if (others > 0) {
      backlog.textContent = `+${others} more`;
      backlog.setAttribute('aria-label', `${others} more events need your answer`);
      backlog.hidden = false;
    } else {
      backlog.hidden = true;
    }

    if (several) {
      // Two <optgroup>s, because the list is ordered unanswered-first and the
      // reason for that order has to be visible inside the control.
      //
      // ⚠ The signal is TEXT — the group labels and a marker in each option
      // string — and it may never become CSS. `<option>` styling is unreliable
      // across browsers and is ignored outright by the native iOS picker, which
      // is a surface this app ships on.
      const rows = events.map((e, i) => {
        const bits = [e.title, scheduleLabel(e)].filter(Boolean);
        const name = opts.groupNames?.get(e.groupId);
        const base = name ? `${bits.join(' · ')} (${name})` : bits.join(' · ');
        const unanswered = isEventUnanswered(e);
        const text = unanswered ? `● ${base}` : base;
        const opt = el('option', { value: String(i) }, text) as HTMLOptionElement;
        if (i === idx) opt.selected = true;
        return { opt, unanswered };
      });

      const needing = rows.filter((r) => r.unanswered).map((r) => r.opt);
      const rest = rows.filter((r) => !r.unanswered).map((r) => r.opt);

      // Only when BOTH exist. A single group whose label describes every row in
      // it tells the reader nothing, and costs a line of the dropdown.
      picker.replaceChildren(
        ...(needing.length && rest.length
          ? [
              el('optgroup', { label: 'Needs your answer' }, needing),
              el('optgroup', { label: 'Coming up' }, rest),
            ]
          : rows.map((r) => r.opt)),
      );
    }

    const chat = el(
      'a',
      { class: 'chat-link', href: `/events/${event.eventId}/chat` },
      '💬 Event chat',
    );
    if (event.chatUnread > 0) {
      chat.append(
        ' ',
        el(
          'span',
          { class: 'chat-unread', 'aria-label': `${event.chatUnread} unread` },
          String(event.chatUnread),
        ),
      );
    }
    // With one event the head's title IS the link to it. With several the picker
    // has taken that slot, and a <select> cannot be a link — so the way into the
    // event has to reappear somewhere, or the panel can switch between events
    // and open none of them.
    chatSlot.replaceChildren(
      ...(several
        ? [
            el('a', { class: 'chat-link', href: `/events/${event.eventId}` }, 'Open event'),
            ' · ',
          ]
        : []),
      chat,
    );

    rsvpSlot.replaceChildren(
      rsvpControl({
        event,
        counts: countsFromSummary(event),
        viewerZone: opts.viewerZone,
        // NOT compact. Compact drops the maybe and no counts, and the PHP panel
        // showed them ("N going · N maybe · N left"). This is an expanded body
        // with room, unlike a card in a dense list.
        compact: false,
        noteTarget: note,
        onChange: (counts) => {
          // Keep the in-memory event in step so collapsing and reopening, or
          // switching away and back, shows the answer just given.
          const live = events[idx];
          if (live && live.eventId === event.eventId) {
            live.yesCount = counts.yesCount;
            live.maybeCount = counts.maybeCount;
            live.noCount = counts.noCount;
            live.dayofConfirmed = counts.dayofConfirmed;
            live.myStatus = counts.myRsvp?.status ?? null;
            // Two fields, two different stories — never derive one from the
            // other. Copied across, not inferred.
            live.myConfirmedDayof = counts.myRsvp?.confirmedDayof ?? false;
            live.myConfirmedDate = counts.myRsvp?.confirmedDate ?? null;
          }
          render();
          opts.onRsvpChange?.(event.eventId, counts);
        },
      }),
    );
  }

  picker.addEventListener('change', () => {
    idx = Number(picker.value) || 0;
    render();
  });

  toggle.addEventListener('click', () => {
    collapsed = !collapsed;
    body.hidden = collapsed;
    toggle.textContent = collapsed ? '▾' : '▴';
    toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    toggle.setAttribute('aria-label', collapsed ? 'Expand' : 'Collapse');
  });

  // Clicking the head anywhere but a control also toggles, which is how a bar
  // this small wants to behave on a phone.
  head.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement | null;
    if (target?.closest('a, button, select')) return;
    toggle.click();
  });

  render();

  return {
    root,
    setEvents(next: EventSummary[]) {
      events = narrow(next);
      // Re-derived, not reset to 0: an event created live must not knock the
      // selection off today's event.
      idx = defaultPinnedIndex(events, now());
      render();
    },
  };
}

/**
 * The events worth pinning: open, not ended, UNANSWERED FIRST, then soonest.
 *
 * A thin wrapper over `orderPinnableEvents()` in `shared/`, kept as the name
 * every view already imports. The rule moved because `web/` has no test runner
 * and the ordering has a tiebreak worth pinning — see docs/plan-next-up.md §3a.
 *
 * ⚠ **`limit` is undefined by default now.** The old hard 5 was half of the
 * original defect: stacked with home's own slice of 5 and a pure date sort, an
 * invite three months out never reached the panel, not because it was far away
 * but because it was sixth. Pass a limit only on a surface that genuinely
 * cannot grow.
 *
 * The PHP panel auto-hid a today event at its end time with a `setTimeout`. The
 * filter uses `eventHasEnded()` instead — the same predicate the RSVP control
 * and the server use, so the panel cannot offer an event the server would
 * refuse.
 */
export function pinnableEvents<T extends EventSummary>(events: T[], limit?: number): T[] {
  return orderPinnableEvents(events, Date.now(), limit);
}
